import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Youtube,
  Facebook,
  Instagram,
  MessageCircle,
  Save,
  Calendar,
  ExternalLink,
  Loader2,
  Settings,
  Link as LinkIcon,
  Globe,
  BookOpen,
  RefreshCw,
  Info
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("social");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingIso, setSavingIso] = useState(false);
  
  // Social Links state
  const [socialLinks, setSocialLinks] = useState({
    youtube: "",
    tiktok: "",
    facebook: "",
    instagram: "",
    whatsapp: "",
    amazon_shop: "",
    google_play: "",
    website: "",
    google_calendar_embed: ""
  });

  // ISO Tab Name state
  const [isoTabName, setIsoTabName] = useState("ISO 9001");

  // Flashcards state
  const [flashcardSubjects, setFlashcardSubjects] = useState([]);
  const [reseedingCards, setReseedingCards] = useState(false);

  const loadFlashcardSubjects = async () => {
    try {
      const res = await axios.get(`${API}/flashcards/subjects`);
      setFlashcardSubjects(res.data || []);
    } catch (err) {
      // silent
    }
  };

  const handleReseedFlashcards = async () => {
    if (!window.confirm(
      "Lernkarten neu importieren?\n\n" +
      "Dies überschreibt die bestehenden Lernkarten mit den 250 Original-Karten aus dem alten Portal " +
      "(braintrainer-app.emergent.host). Der Lernfortschritt aller Teilnehmer wird zurückgesetzt."
    )) {
      return;
    }
    setReseedingCards(true);
    try {
      const res = await axios.post(`${API}/flashcards/seed?force=true`);
      toast.success(res.data.message || "Lernkarten neu importiert");
      await loadFlashcardSubjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Re-Import");
    } finally {
      setReseedingCards(false);
    }
  };

  useEffect(() => {
    loadData();
    loadFlashcardSubjects();
  }, []);

  const loadData = async () => {
    try {
      const [linksRes, isoRes] = await Promise.all([
        axios.get(`${API}/settings/social-links`),
        axios.get(`${API}/settings/iso-tab-name`),
      ]);
      setSocialLinks(linksRes.data);
      setIsoTabName(isoRes.data.name || "ISO 9001");
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSocialLinks = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/social-links`, socialLinks);
      toast.success("Einstellungen gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const saveIsoTabName = async () => {
    if (!isoTabName.trim()) { toast.error("Name darf nicht leer sein"); return; }
    setSavingIso(true);
    try {
      await axios.put(`${API}/settings/iso-tab-name`, null, { params: { name: isoTabName } });
      toast.success("Tab-Name gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingIso(false);
    }
  };

  // Extract calendar ID from various Google Calendar URL formats
  const getCalendarPreviewUrl = () => {
    const embed = socialLinks.google_calendar_embed;
    if (!embed) return null;
    
    // If it's already an iframe src URL
    if (embed.includes('calendar.google.com/calendar/embed')) {
      return embed;
    }
    
    // If it's a calendar ID (e.g., xxx@group.calendar.google.com)
    if (embed.includes('@')) {
      return `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(embed)}&ctz=Europe/Berlin`;
    }
    
    // If it's an ICS URL
    if (embed.includes('.ics')) {
      // Can't embed ICS directly, show info
      return null;
    }
    
    return embed;
  };

  if (loading) {
    return (
      <AdminLayout title="Einstellungen">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Einstellungen">
      <div className="space-y-6" data-testid="settings-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 bg-secondary/50">
            <TabsTrigger value="social" data-testid="social-tab-settings" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <LinkIcon className="h-4 w-4" />
              Social Media
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="calendar-tab-settings" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Calendar className="h-4 w-4" />
              Google Kalender
            </TabsTrigger>
            <TabsTrigger value="flashcards" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="flashcards-tab-settings">
              <BookOpen className="h-4 w-4" />
              Lernkarten
            </TabsTrigger>
            <TabsTrigger value="iso" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="iso-tab-settings">
              <Settings className="h-4 w-4" />
              QM-Reiter
            </TabsTrigger>
          </TabsList>

          {/* SOCIAL MEDIA TAB */}
          <TabsContent value="social" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  Social-Media-Links verwalten
                </CardTitle>
                <Button onClick={saveSocialLinks} disabled={saving} className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Speichern
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Hinterlegen Sie hier die Links zu Ihren Social-Media-Profilen. 
                  Nur ausgefüllte Links werden den Teilnehmern als Icons angezeigt.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* YouTube */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Youtube className="h-4 w-4 text-red-500" />
                      YouTube
                    </Label>
                    <Input
                      value={socialLinks.youtube || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, youtube: e.target.value }))}
                      placeholder="https://youtube.com/@IhrKanal"
                      className="bg-background"
                    />
                  </div>

                  {/* TikTok */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <TikTokIcon className="h-4 w-4" />
                      TikTok
                    </Label>
                    <Input
                      value={socialLinks.tiktok || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, tiktok: e.target.value }))}
                      placeholder="https://tiktok.com/@IhrProfil"
                      className="bg-background"
                    />
                  </div>

                  {/* Facebook */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Facebook className="h-4 w-4 text-blue-600" />
                      Facebook
                    </Label>
                    <Input
                      value={socialLinks.facebook || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
                      placeholder="https://facebook.com/IhreSeite"
                      className="bg-background"
                    />
                  </div>

                  {/* Instagram */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Instagram className="h-4 w-4 text-pink-500" />
                      Instagram
                    </Label>
                    <Input
                      value={socialLinks.instagram || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
                      placeholder="https://instagram.com/IhrProfil"
                      className="bg-background"
                    />
                  </div>

                  {/* WhatsApp */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-green-500" />
                      WhatsApp
                    </Label>
                    <Input
                      value={socialLinks.whatsapp || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, whatsapp: e.target.value }))}
                      placeholder="https://wa.me/49123456789"
                      className="bg-background"
                    />
                  </div>

                  {/* Amazon Shop */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-orange-500" />
                      Amazon Shop
                    </Label>
                    <Input
                      value={socialLinks.amazon_shop || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, amazon_shop: e.target.value }))}
                      placeholder="https://amazon.de/shop/IhrShop"
                      className="bg-background"
                    />
                  </div>

                  {/* Google Play */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-green-600" />
                      Google Play Store
                    </Label>
                    <Input
                      value={socialLinks.google_play || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, google_play: e.target.value }))}
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      className="bg-background"
                    />
                  </div>

                  {/* Website */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-cyan-500" />
                      Webseite
                    </Label>
                    <Input
                      value={socialLinks.website || ""}
                      onChange={(e) => setSocialLinks(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://www.stbrain-trainerimgbr.de"
                      className="bg-background"
                      data-testid="website-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* GOOGLE CALENDAR TAB */}
          <TabsContent value="calendar" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Google Kalender einbetten
                </CardTitle>
                <Button onClick={saveSocialLinks} disabled={saving} className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Speichern
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Instructions */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-medium text-blue-500">So erhalten Sie den Google Kalender Link:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Öffnen Sie <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">Google Kalender</a></li>
                        <li>Klicken Sie auf ⚙️ Einstellungen → Kalender-Einstellungen</li>
                        <li>Wählen Sie Ihren Kalender aus</li>
                        <li>Scrollen Sie zu "Kalender integrieren"</li>
                        <li>Kopieren Sie die <strong>"Öffentliche URL zu diesem Kalender"</strong> oder den <strong>"Einbettungscode"</strong></li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    Google Kalender Link / Embed-Code / ICS-URL
                  </Label>
                  <Textarea
                    value={socialLinks.google_calendar_embed || ""}
                    onChange={(e) => setSocialLinks(prev => ({ ...prev, google_calendar_embed: e.target.value }))}
                    placeholder="https://calendar.google.com/calendar/embed?src=... oder ICS-Link"
                    className="bg-background resize-none font-mono text-sm"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Akzeptierte Formate: Google Calendar Embed-URL, Kalender-ID (xxx@group.calendar.google.com), oder ICS-Link
                  </p>
                </div>

                {/* Preview */}
                {socialLinks.google_calendar_embed && (
                  <div className="space-y-2">
                    <Label>Vorschau</Label>
                    <div className="border border-border rounded-lg overflow-hidden bg-white">
                      {getCalendarPreviewUrl() ? (
                        <iframe
                          src={getCalendarPreviewUrl()}
                          style={{ border: 0 }}
                          width="100%"
                          height="400"
                          frameBorder="0"
                          scrolling="no"
                          title="Google Kalender Vorschau"
                        />
                      ) : (
                        <div className="p-8 text-center text-muted-foreground">
                          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>ICS-Links können nicht als Vorschau angezeigt werden.</p>
                          <p className="text-sm mt-2">Der Kalender wird den Teilnehmern als Download-Link angeboten.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!socialLinks.google_calendar_embed && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Noch kein Kalender eingebettet</p>
                    <p className="text-sm mt-2">Fügen Sie oben einen Google Kalender Link ein.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FLASHCARDS TAB */}
          <TabsContent value="flashcards" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  Lernkarten-Verwaltung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {flashcardSubjects.map(s => (
                    <div key={s.id} className="p-3 bg-secondary/30 rounded-md text-center" data-testid={`flashcard-subject-${s.id}`}>
                      <div className="text-2xl mb-1">{s.icon || "📚"}</div>
                      <div className="font-semibold text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.card_count ?? 0} Karten</div>
                    </div>
                  ))}
                </div>
                <div className="p-4 rounded-md bg-secondary/30 border border-border">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-primary" />
                    Original-Lernkarten aus dem alten Portal
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    250 Original-Lernkarten (5 Fächer × 50 Karten) wurden aus
                    <code className="mx-1 px-1 py-0.5 bg-background rounded">braintrainer-app.emergent.host</code>
                    übernommen. Beim Re-Import werden alle Karten überschrieben und der Lernfortschritt aller Teilnehmer zurückgesetzt.
                  </p>
                  <Button
                    onClick={handleReseedFlashcards}
                    disabled={reseedingCards}
                    variant="outline"
                    className="gap-2"
                    data-testid="reseed-flashcards-btn"
                  >
                    {reseedingCards ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Lernkarten neu importieren
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ISO TAB NAME TAB */}
          <TabsContent value="iso" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  QM-Reiter benennen
                </CardTitle>
                <Button onClick={saveIsoTabName} disabled={savingIso} className="gap-2" data-testid="save-iso-tab-name">
                  {savingIso ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Speichern
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Legen Sie den Namen des Qualitätsmanagement-Reiters in der Seitenleiste fest. 
                  Dieser Name ist jederzeit frei änderbar.
                </p>
                <div className="space-y-2 max-w-md">
                  <Label className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-muted-foreground" />
                    Reiter-Name
                  </Label>
                  <Input
                    value={isoTabName}
                    onChange={(e) => setIsoTabName(e.target.value)}
                    placeholder="z.B. ISO 9001, Zertifizierung, QM-System"
                    className="bg-background"
                    data-testid="iso-tab-name-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Wird in der Seitenleiste als "{isoTabName} QM" angezeigt
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
